import { createElement } from 'react';
import { Action } from '@wix/component-protocol/schema';
import {
  IActionPanelAPI,
  NativeActionsList,
  StylesDerivedNativeActionNames,
} from '@wix/editor-platform-host-integration-apis';
import { GlobalDesignPanel } from '../../../../ui/globalDesignPanel';
import { PlatformContext } from '../types';
import { isNativeStylesActionName } from '../utils';
import { getPlatformInfraAPI } from '../../../bootstrap/init';
import { IManifestContextBuilder } from '@wix/editor-platform-manifest-services';
import { buildGlobalDesignContext } from './globalDesign/contextBuilder';
import { AutomaticPanel } from '../../../../ui/automaticPanel/AutomaticPanel';
import { createContextBuilder } from '../customActions/contextBuilder';
import { EditorPointer } from '@wix/editorx-core-api';
import { CompRef } from '@wix/document-services-types';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

export type ExecutionContext = {
  namespace: 'data' | 'cssProperties' | 'cssCustomProperties';
  itemKeys: string[];
};

class GlobalDesignActionPanelAPI implements IActionPanelAPI {
  constructor(
    readonly editorPointer: EditorPointer<CompRef>,
    readonly actionName: StylesDerivedNativeActionNames,
  ) {}

  public getContent = () => {
    return createElement(
      GlobalDesignPanel,
      {
        editorPointer: this.editorPointer,
        actionName: this.actionName,
        getHost: this.getHost,
      },
      null,
    );
  };

  public getHostContext = async (props?: {
    contextBuilder?: IManifestContextBuilder;
  }) => {
    const infra = await getPlatformInfraAPI();
    return infra.getSDKHostContext({
      contextBuilder:
        props?.contextBuilder ??
        buildGlobalDesignContext(this.editorPointer, this.actionName),
      ...props,
    });
  };

  /**
   * @deprecated use `getHostContext` instead
   */
  public getHost = async (props?: {
    contextBuilder?: IManifestContextBuilder;
  }) => {
    const hostContext = await this.getHostContext(props);
    return hostContext.getHost();
  };
}

class CustomActionPanelAPI implements IActionPanelAPI {
  readonly namespace: 'data' | 'cssProperties' | 'cssCustomProperties';
  readonly panelType: 'settings' | 'design';

  constructor(
    readonly editorPointer: EditorPointer<CompRef>,
    readonly action: Action,
    readonly actionName: string,
    readonly executionContext: ExecutionContext,
  ) {
    this.namespace = this.executionContext.namespace;
    this.panelType = this.namespace === 'data' ? 'settings' : 'design';
  }

  getItemsKeys = (): string[] => {
    return this.executionContext.itemKeys;
  };

  getHostContext = async (props?: {
    contextBuilder?: IManifestContextBuilder;
  }) => {
    const infra = await getPlatformInfraAPI();
    const itemsKeys = this.getItemsKeys();
    return infra.getSDKHostContext({
      contextBuilder: createContextBuilder(
        this.editorPointer,
        this.namespace,
        itemsKeys,
      ),
      ...props,
    });
  };

  getHost = async () => {
    const hostContext = await this.getHostContext();
    return hostContext?.getHost();
  };

  getContent() {
    return createElement(AutomaticPanel, {
      getHost: this.getHost,
      panelType: this.panelType,
    });
  }
}

class UnknownActionPanelAPI implements IActionPanelAPI {
  constructor(
    readonly editorPointer: EditorPointer<CompRef>,
    readonly action: Action,
    readonly actionName: string,
  ) {}

  getContent() {
    return undefined;
  }

  getHostContext = async (props?: {
    contextBuilder?: IManifestContextBuilder;
    editorPointer?: EditorPointer<CompRef>;
  }) => {
    const infra = await getPlatformInfraAPI();
    return infra.getSDKHostContext({
      contextBuilder: props?.contextBuilder,
      editorPointer: props?.editorPointer || this.editorPointer,
    });
  };

  getHost = async () => {
    const hostContext = await this.getHostContext();
    return hostContext.getHost();
  };
}

class EditTextActionPanelAPI implements IActionPanelAPI {
  constructor(readonly context: PlatformContext) {}

  getContent() {
    return null;
  }

  getHostContext = async (props?: {
    editorPointer?: EditorPointer<CompRef>;
  }) => {
    const infra = await getPlatformInfraAPI();
    return infra.getSDKHostContext(
      props?.editorPointer
        ? {
            contextBuilder: (builder) => {
              builder.isStageBound(true);
              builder.withRuntimeMappings(false);

              builder.byEditorPointer(props.editorPointer!, (manifest) => {
                const textTypeData = Object.fromEntries(
                  Object.entries(manifest.data ?? {}).flatMap(([key, data]) =>
                    data.dataType === DataType.text ? [[key, data]] : [],
                  ),
                );

                builder.withManifest(props.editorPointer!, {
                  editorElement: {
                    displayName: manifest.displayName,
                    data: textTypeData,
                  },
                });
              });
            },
          }
        : {},
    );
  };

  getHost = async (props?: { editorPointer?: EditorPointer<CompRef> }) => {
    const hostContext = await this.getHostContext(props);
    return hostContext.getHost();
  };
}

export function createActionPanelAPI(
  action: Action,
  actionName: string,
  context: PlatformContext,
  executionContext?: ExecutionContext,
):
  | GlobalDesignActionPanelAPI
  | CustomActionPanelAPI
  | UnknownActionPanelAPI
  | EditTextActionPanelAPI {
  if (
    (action.execution?.data ||
      action.execution?.cssProperty ||
      action.execution?.cssCustomProperty ||
      action.execution?.displayGroup) &&
    executionContext
  ) {
    return new CustomActionPanelAPI(
      context.editorPointer,
      action,
      actionName,
      executionContext,
    );
  }

  if (actionName === NativeActionsList.editText) {
    return new EditTextActionPanelAPI(context);
  }

  if (isNativeStylesActionName(actionName)) {
    return new GlobalDesignActionPanelAPI(context.editorPointer, actionName);
  }

  return new UnknownActionPanelAPI(context.editorPointer, action, actionName);
}
